import { useEffect, useMemo, useState } from 'react';

import { useParams } from 'react-router-dom';

import { Box, Grid, Stack, Typography } from '@mui/material';
import useImages from 'api/useImages';
import usePermissions from 'api/usePermissions';
import useTheme from 'api/useTheme';
import { EAssetType, EFeatureFlag } from 'constants/enums';
import { useFormikContext } from 'formik';
import { UploadsTooltips } from 'hooks/useUpload';
import { Base64 } from 'js-base64';
import { extractColorValue } from 'utils/colorUtils';

import AcCard from 'components/AcCard/AcCard';
import AcSelect from 'components/AcSelect/AcSelect';
import AcSolidInput from 'components/AcSolidInput/AcSolidInput';
import ActionButton from 'components/ActionButton/ActionButton';

import { EToasterStatus, showToast } from '../../../utils/showToast';
import { ThemeLoader } from '../ThemeLoader';
import { validateImageRatio } from '../utils';

import { CheckoutLegalText } from './CheckoutLegalText';
import CheckoutThemePreviewV3 from './Previews/CheckoutThemePreviewV3';
import CheckoutThemePreviewV2 from './Previews/CheckoutThemePreviewV2';
import {
  checkoutTextColorOptions,
  defaultCheckoutTheme,
  getDefaultFontColorByVersion
} from './defaults';
import { CheckoutThemeFormValues } from './types';

import 'style/forms.scss';
import useFeatureFlags from 'api/useFeatureFlags';

const CheckoutTheme = () => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { isSuperAdminByProject } = usePermissions();

  const { values, handleBlur, handleChange, setFieldValue, errors, touched } =
    useFormikContext<CheckoutThemeFormValues>();
  const { publisherId } = useParams();
  const { featureFlags } = useFeatureFlags({ publisherId });

  const isCheckoutV3Enabled = useMemo(
    () => featureFlags?.[EFeatureFlag.CHECKOUT_V3],
    [featureFlags]
  );

  const getDefaults = () => {
    return {
      footerConfiguration: defaultCheckoutTheme.footerConfiguration,
      logo: defaultCheckoutTheme.logo,
      primaryColor: defaultCheckoutTheme.primaryColor,
      textColor: defaultCheckoutTheme.textColor,
      backgroundImage: defaultCheckoutTheme.backgroundImage,
      mobileBgImage: defaultCheckoutTheme.mobileBgImage,
      buttonColor: defaultCheckoutTheme.buttonColor,
      buttonTextColor: defaultCheckoutTheme.buttonTextColor,
      publisherId: publisherId || '',
      _id: ''
    };
  };

  const defaultValues = useMemo(() => {
    const defaults = getDefaults();

    if (!values) {
      return defaults;
    }

    return {
      ...values,
      logo: values.logo || defaults.logo,
      primaryColor:
        extractColorValue(values.primaryColor) || defaults.primaryColor,
      textColor: getDefaultFontColorByVersion(
        isCheckoutV3Enabled,
        values.textColor
      ),
      footerConfiguration: {
        DEFAULT: {
          richText: Base64.encode('')
        },
        ...(values.footerConfiguration ?? {}),
        ...(defaults.footerConfiguration ?? {})
      },
      backgroundImage: values.backgroundImage || defaults.backgroundImage,
      mobileBgImage: values.mobileBgImage || defaults.mobileBgImage,
      buttonColor:
        extractColorValue(values.buttonColor) || defaults.buttonColor,
      buttonTextColor:
        extractColorValue(values.buttonTextColor) || defaults.buttonTextColor,
      publisherId: values.publisherId || defaults.publisherId,
      _id: values._id || defaults._id
    };
  }, [values, publisherId, isCheckoutV3Enabled]);

  const { getCheckoutTheme: checkoutTheme } = useTheme(publisherId);

  const pictures = useImages(publisherId).getImages;
  const [showPreview, setShowPreview] = useState(false);

  const hasValues = useMemo(
    () => values && Object.keys(values).length > 0,
    [values]
  );

  useEffect(
    () =>
      setIsLoading(
        checkoutTheme.isLoading || !checkoutTheme.data || !hasValues
      ),
    [checkoutTheme?.isLoading, checkoutTheme?.data, hasValues]
  );

  if (isLoading) {
    return <ThemeLoader />;
  }

  return (
    <>
      {showPreview ? (
        isCheckoutV3Enabled ? (
          <CheckoutThemePreviewV3
            fontFamily={'Inter'}
            logo={defaultValues.logo}
            bgImage={defaultValues.backgroundImage}
            bgMobileImage={defaultValues.mobileBgImage}
            primaryColor={defaultValues.primaryColor}
            textColor={defaultValues.textColor}
            onClose={() => setShowPreview(false)}
          />
        ) : (
          <CheckoutThemePreviewV2
            logo={defaultValues.logo}
            bgImage={defaultValues.backgroundImage}
            bgMobileImage={defaultValues.mobileBgImage}
            primaryColor={defaultValues.primaryColor}
            textColor={defaultValues.textColor}
            buttonColor={defaultValues.buttonColor}
            buttonTextColor={defaultValues.buttonTextColor}
            onClose={() => setShowPreview(false)}
          />
        )
      ) : pictures.isLoading ? (
        <ThemeLoader />
      ) : (
        <Stack className="formContent" width="100%">
          <Stack direction="row" justifyContent="space-between">
            <Grid container>
              <Grid item xs={5}>
                <Typography
                  variant="h6"
                  sx={{
                    marginBottom: '12px',
                    fontFamily: 'Inter, Montserrat, serif',
                    fontWeight: '600'
                  }}
                >
                  Checkout Page
                </Typography>
                <Stack gap={3}>
                  <AcCard stackContainer={false}>
                    <Grid container rowSpacing={2} columnSpacing={1.5}>
                      <Grid item xs={12}>
                        <AcSelect
                          header="Logo"
                          name="logo"
                          value={defaultValues.logo}
                          dataTestBoxId="checkoutLogo"
                          uploadBtnTestId="uploadCheckoutLogo"
                          dataTestListId="checkoutLogoList"
                          optionDataTestIdPrefix="checkoutLogoOption"
                          uploadConfig={{
                            onUploadSuccess: async (url: string) => {
                              await pictures.refetch();
                              setFieldValue('checkout.logo', url);
                            },
                            uploadType: EAssetType.LOGO,
                            uploadMessage: UploadsTooltips[EAssetType.LOGO]
                          }}
                          items={[
                            {
                              content: 'Default Appcharge logo',
                              key: 'https://media.appcharge.com/defaults/default-store-logo.svg',
                              value:
                                'https://media.appcharge.com/defaults/default-store-logo.svg',
                              renderFunction: () => {
                                return (
                                  <Stack
                                    direction="row"
                                    alignItems="center"
                                    gap={1}
                                  >
                                    <img
                                      src="https://media.appcharge.com/defaults/default-store-logo.svg"
                                      alt="Default Appcharge logo"
                                      style={{
                                        maxWidth: 26
                                      }}
                                    />
                                    <Typography>
                                      Default Appcharge logo
                                    </Typography>
                                  </Stack>
                                );
                              }
                            },
                            ...(pictures.data || [])
                              .filter((p: any) => p.type === EAssetType.LOGO)
                              .map((picture: any) => {
                                return {
                                  content: picture.name,
                                  key: picture.url,
                                  value: picture.url,
                                  renderFunction: () => {
                                    return (
                                      <Stack
                                        direction="row"
                                        alignItems="center"
                                        gap={1}
                                      >
                                        <img
                                          src={picture.url}
                                          alt={picture.name}
                                          style={{
                                            width: 26,
                                            maxHeight: 30
                                          }}
                                        />
                                        <Typography>{picture.name}</Typography>
                                      </Stack>
                                    );
                                  }
                                };
                              })
                          ]}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={touched.logo && Boolean(errors.logo)}
                          tooltip="This will be the logo of your checkout."
                        />
                      </Grid>
                      <Grid item xs={12}>
                        {!isCheckoutV3Enabled && (
                          <AcSolidInput
                            header="Primary Color"
                            name="primaryColor"
                            defaultValue={defaultValues.primaryColor}
                            setValue={setFieldValue}
                            onBlur={handleBlur}
                            dataTestId="checkoutPrimaryColor"
                            dataTestPopoverId="checkoutPrimaryColorPopover"
                            dataTestColorPickerId="checkoutPrimaryColorPicker"
                            tooltip="This will be the primary color of your checkout. It will appear behind the background image in case no background image is present."
                            error={
                              touched.primaryColor &&
                              Boolean(errors.primaryColor)
                            }
                            helperText={
                              touched.primaryColor
                                ? errors.primaryColor?.toString()
                                : ''
                            }
                          />
                        )}
                        <Box mt={3}>
                          {isCheckoutV3Enabled ? (
                            <AcSelect
                              header="Font Color"
                              name="textColor"
                              value={defaultValues.textColor}
                              items={checkoutTextColorOptions}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              dataTestBoxId="checkoutTextColor"
                              dataTestListId="checkoutTextColorList"
                              optionDataTestIdPrefix="checkoutTextColorOption"
                              error={
                                touched.textColor && Boolean(errors.textColor)
                              }
                              helperText={
                                touched.textColor
                                  ? errors.textColor?.toString()
                                  : ''
                              }
                            />
                          ) : (
                            <AcSolidInput
                              header="Font Color"
                              name="textColor"
                              defaultValue={defaultValues.textColor}
                              setValue={setFieldValue}
                              onBlur={handleBlur}
                              dataTestId="checkoutTextColor"
                              dataTestPopoverId="checkoutTextColorPopover"
                              dataTestColorPickerId="checkoutTextColorPicker"
                              error={
                                touched.textColor && Boolean(errors.textColor)
                              }
                              helperText={
                                touched.textColor
                                  ? errors.textColor?.toString()
                                  : ''
                              }
                            />
                          )}
                        </Box>
                      </Grid>
                      <Grid item xs={12}>
                        <AcSelect
                          header="Background Image"
                          name="backgroundImage"
                          value={defaultValues.backgroundImage}
                          dataTestBoxId="checkoutBackgroundImage"
                          uploadBtnTestId="uploadCheckoutBackgroundImage"
                          dataTestListId="checkoutBackgroundImageList"
                          optionDataTestIdPrefix="checkoutBackgroundImageOption"
                          onClear={() => {
                            setFieldValue('checkout.backgroundImage', '');
                          }}
                          uploadConfig={{
                            onUploadSuccess: async (url: string) => {
                              await pictures.refetch();
                              setFieldValue('checkout.backgroundImage', url);
                            },
                            uploadType: EAssetType.BG_DESKTOP,
                            validator: async (file) => {
                              const isValid = await validateImageRatio(
                                file,
                                1 / 2
                              );
                              if (!isValid) {
                                showToast({
                                  message: 'Image aspect ratio must be 1:2',
                                  status: EToasterStatus.ERROR
                                });
                              }
                              return isValid;
                            },
                            uploadMessage: UploadsTooltips.BG_CHECKOUT
                          }}
                          items={[
                            {
                              content: 'Default Appcharge background',
                              key: 'https://media.appcharge.com/defaults/background.png',
                              value:
                                'https://media.appcharge.com/defaults/background.png',
                              renderFunction: () => {
                                return (
                                  <Stack
                                    direction="row"
                                    alignItems="center"
                                    gap={1}
                                  >
                                    <img
                                      src="https://media.appcharge.com/defaults/background.png"
                                      alt="Default Appcharge background"
                                      style={{
                                        width: 30,
                                        maxHeight: 30
                                      }}
                                    />
                                    <Typography>
                                      Default Appcharge background
                                    </Typography>
                                  </Stack>
                                );
                              }
                            },
                            ...(pictures.data || [])
                              .filter(
                                (p: any) => p.type === EAssetType.BG_DESKTOP
                              )
                              .map((picture: any) => {
                                return {
                                  content: picture.name,
                                  key: picture.url,
                                  value: picture.url,
                                  renderFunction: () => {
                                    return (
                                      <Stack
                                        direction="row"
                                        alignItems="center"
                                        gap={1}
                                      >
                                        <img
                                          src={picture.url}
                                          alt={picture.name}
                                          style={{
                                            width: 30,
                                            maxHeight: 30
                                          }}
                                        />
                                        <Typography>{picture.name}</Typography>
                                      </Stack>
                                    );
                                  }
                                };
                              })
                          ]}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={
                            touched.backgroundImage &&
                            Boolean(errors.backgroundImage)
                          }
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <AcSelect
                          header="Background Image (Mobile)"
                          name="mobileBgImage"
                          displayEmpty
                          value={
                            defaultValues.mobileBgImage ||
                            'No Background (Taken from Desktop)'
                          }
                          dataTestBoxId="checkoutMobileBgImage"
                          uploadBtnTestId="uploadCheckoutMobileBgImage"
                          dataTestListId="checkoutMobileBgImageList"
                          optionDataTestIdPrefix="checkoutMobileBgImageOption"
                          onClear={() => {
                            setFieldValue('checkout.mobileBgImage', '');
                          }}
                          uploadConfig={{
                            onUploadSuccess: async (url: string) => {
                              await pictures.refetch();
                              setFieldValue('checkout.mobileBgImage', url);
                            },
                            uploadType: EAssetType.BG_MOBILE,
                            validator: async (file) => {
                              const isValid = await validateImageRatio(
                                file,
                                1 / 2
                              );
                              if (!isValid) {
                                showToast({
                                  message: 'Image aspect ratio must be 1:2',
                                  status: EToasterStatus.ERROR
                                });
                              }
                              return isValid;
                            },
                            uploadMessage: UploadsTooltips.BG_CHECKOUT
                          }}
                          items={[
                            {
                              content: 'No Background (Taken from Desktop)',
                              key: '',
                              value: '',
                              renderFunction: () => {
                                return (
                                  <Stack
                                    direction="row"
                                    alignItems="center"
                                    gap={1}
                                  >
                                    <Typography>
                                      No Background (Taken from Desktop)
                                    </Typography>
                                  </Stack>
                                );
                              }
                            },
                            ...(pictures.data || [])
                              .filter(
                                (p: any) => p.type === EAssetType.BG_MOBILE
                              )
                              .map((picture: any) => {
                                return {
                                  content: picture.name,
                                  key: picture.url,
                                  value: picture.url,
                                  renderFunction: () => {
                                    return (
                                      <Stack
                                        direction="row"
                                        alignItems="center"
                                        gap={1}
                                      >
                                        <img
                                          src={picture.url}
                                          alt={picture.name}
                                          style={{
                                            width: 30,
                                            maxHeight: 30
                                          }}
                                        />
                                        <Typography>{picture.name}</Typography>
                                      </Stack>
                                    );
                                  }
                                };
                              })
                          ]}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={
                            touched.mobileBgImage &&
                            Boolean(errors.mobileBgImage)
                          }
                        />
                      </Grid>
                      {!isCheckoutV3Enabled && (
                        <Grid item xs={12} mt={0}>
                          <AcSolidInput
                            header="Button Color"
                            name="buttonColor"
                            defaultValue={defaultValues.buttonColor}
                            setValue={setFieldValue}
                            onBlur={handleBlur}
                            dataTestId="checkoutButtonColor"
                            dataTestPopoverId="checkoutButtonColorPopover"
                            dataTestColorPickerId="checkoutButtonColorPicker"
                            error={
                              touched.buttonColor && Boolean(errors.buttonColor)
                            }
                            helperText={
                              touched.buttonColor
                                ? errors.buttonColor?.toString()
                                : ''
                            }
                          />
                          <Box mt={3}>
                            <AcSolidInput
                              header="Button Font Color"
                              name="buttonTextColor"
                              defaultValue={defaultValues.buttonTextColor}
                              setValue={setFieldValue}
                              onBlur={handleBlur}
                              dataTestId="checkoutButtonTextColor"
                              dataTestPopoverId="checkoutButtonTextColorPopover"
                              dataTestColorPickerId="checkoutButtonTextColorPicker"
                              error={
                                touched.buttonTextColor &&
                                Boolean(errors.buttonTextColor)
                              }
                              helperText={
                                touched.textColor
                                  ? errors.textColor?.toString()
                                  : ''
                              }
                            />
                          </Box>
                        </Grid>
                      )}
                      {isSuperAdminByProject(publisherId || '') && (
                        <Grid item xs={12}>
                          <CheckoutLegalText
                            values={values}
                            setFieldValue={setFieldValue}
                            defaultValues={defaultValues}
                          />
                        </Grid>
                      )}
                    </Grid>
                  </AcCard>
                </Stack>
                <Box mt={4}>
                  <ActionButton
                    variant="outlined"
                    size="small"
                    onClick={() => setShowPreview(true)}
                    sx={{
                      textTransform: 'capitalize'
                    }}
                    text="Preview (Web and Mobile)"
                    dataTestId="checkoutPreviewButton"
                  />
                </Box>
              </Grid>
            </Grid>
          </Stack>
        </Stack>
      )}
    </>
  );
};

export default CheckoutTheme;
